import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useRecentWorkouts,
  type ActualWorkout,
  type Discipline,
} from '@/lib/workouts';
import { formatRelativeTime } from '@/lib/time';
import { disciplineLabel } from './WorkoutCard';

/**
 * Card "Últimas actividades" del dashboard (M2.7).
 *
 * Decisiones:
 *  - Listado vertical compacto: cada entrada es un `<Link>` al detalle del
 *    workout. Esto convierte cada fila en un target táctil >=44x44px en
 *    mobile, cumpliendo touch-target accesible.
 *  - El emoji por disciplina es decorativo (`aria-hidden`); el `aria-label`
 *    de la fila ya menciona la disciplina en español.
 *  - El tiempo relativo se calcula con `formatRelativeTime` de `lib/time.ts`.
 *  - La distancia es opcional: solo la mostramos si el backend la trae
 *    (`distanceM != null`).
 *  - Source badge: solo lo mostramos cuando `source !== 'manual'` para no
 *    saturar — el caso por defecto en triatlón es Strava, marcamos esa
 *    visualmente como referencia.
 */
export function RecentActivitiesCard({ limit = 8 }: { limit?: number }): ReactElement {
  const query = useRecentWorkouts(limit);

  return (
    <article
      aria-label="Últimas actividades"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Últimas actividades
        </h2>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="mt-3 text-sm text-slate-600">
          Cargando actividades…
        </p>
      )}

      {query.isError && (
        <p role="alert" className="mt-3 text-sm text-rose-700">
          No se pudieron cargar las últimas actividades.
        </p>
      )}

      {query.data && <RecentList items={query.data.actual} />}
    </article>
  );
}

function RecentList({ items }: { items: ActualWorkout[] }): ReactElement {
  if (items.length === 0) {
    return (
      <p
        data-testid="recent-empty"
        className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50 p-3 text-center text-sm text-slate-600"
      >
        Sin actividades aún. Conecta Strava o registra una manualmente.
      </p>
    );
  }
  return (
    <ul aria-label="Actividades recientes" className="mt-3 divide-y divide-slate-100">
      {items.map((w) => (
        <RecentItem key={w.id} workout={w} />
      ))}
    </ul>
  );
}

function RecentItem({ workout }: { workout: ActualWorkout }): ReactElement {
  const minutes = Math.round(workout.durationMin);
  const disc = disciplineLabel(workout.discipline);
  const distance =
    typeof workout.distanceM === 'number' && workout.distanceM > 0
      ? `${formatKm(workout.distanceM / 1000)} km`
      : null;
  const rel = formatRelativeTime(workout.datetime);

  // M2.10 — badges contextuales sin saturar:
  //  - Desnivel: solo a partir de +100m (criterio: una sesión "llana" en
  //    ciudad típicamente queda por debajo; >100m ya señaliza ruta con
  //    perfil relevante).
  //  - Normalized Power: solo en bike (es el dato distintivo de ciclismo;
  //    en swim/run/strength NP no aplica en triatlón estándar).
  const elevation =
    typeof workout.elevationGainM === 'number' &&
    workout.elevationGainM !== null &&
    workout.elevationGainM > 100
      ? workout.elevationGainM
      : null;
  const np =
    workout.discipline === 'bike' &&
    typeof workout.normalizedPower === 'number' &&
    workout.normalizedPower !== null &&
    workout.normalizedPower > 0
      ? workout.normalizedPower
      : null;

  const ariaLabel = [
    disc,
    `${minutes} minutos`,
    rel,
    distance ?? null,
    elevation !== null ? `desnivel ${elevation} metros` : null,
    np !== null ? `NP ${np} vatios` : null,
  ]
    .filter((p): p is string => p !== null)
    .join(', ');

  return (
    <li>
      <Link
        to="/entreno/$id"
        params={{ id: String(workout.id) }}
        aria-label={ariaLabel}
        className="flex items-center gap-3 px-1 py-2 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
      >
        <span
          aria-hidden="true"
          className="text-xl leading-none"
          data-testid="recent-emoji"
        >
          {disciplineEmoji(workout.discipline)}
        </span>
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-medium text-slate-900">
            {minutes} min {disc.toLowerCase()}
            {distance && <span className="text-slate-600"> · {distance}</span>}
            {elevation !== null && (
              <span
                className="ml-1 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-900"
                data-testid="recent-elevation-badge"
              >
                +{elevation}m
              </span>
            )}
            {np !== null && (
              <span
                className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-900"
                data-testid="recent-np-badge"
              >
                NP {np}w
              </span>
            )}
          </p>
          <p className="text-xs text-slate-500">{rel}</p>
        </div>
        {workout.source !== 'manual' && (
          <span
            className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-orange-900"
            aria-label={`Origen: ${workout.source}`}
            data-testid="recent-source-badge"
          >
            {workout.source}
          </span>
        )}
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function disciplineEmoji(d: Discipline): string {
  switch (d) {
    case 'swim': return '🏊';
    case 'bike': return '🚴';
    case 'run': return '🏃';
    case 'strength': return '💪';
    case 'brick': return '🔁';
    case 'other': return '🏅';
  }
}

function formatKm(km: number): string {
  if (km >= 100) return Math.round(km).toString();
  if (km >= 10) return km.toFixed(1);
  return km.toFixed(2);
}
